import type { Scene } from "@babylonjs/core/scene";
import type { Game } from "./Game";

export interface GameScene {
  /** The underlying Babylon scene, or null if this "scene" is DOM-only. */
  readonly babylonScene: Scene | null;
  enter(): Promise<void> | void;
  leave(): Promise<void> | void;
  dispose(): void;
}

type SceneFactory = (arg?: unknown) => GameScene;

export class SceneManager {
  private factories = new Map<string, SceneFactory>();
  private current?: { name: string; scene: GameScene };

  constructor(readonly game: Game) {}

  register(name: string, factory: SceneFactory) {
    this.factories.set(name, factory);
  }

  get activeBabylonScene() {
    return this.current?.scene.babylonScene ?? null;
  }

  get activeName() {
    return this.current?.name;
  }

  async go(name: string, arg?: unknown) {
    const factory = this.factories.get(name);
    if (!factory) throw new Error(`Unknown scene: ${name}`);

    if (this.current) {
      await this.current.scene.leave();
      this.current.scene.dispose();
      this.clearUi();
    }

    const scene = factory(arg);
    this.current = { name, scene };
    await scene.enter();
  }

  private clearUi() {
    // Every scene is expected to clean its own UI, but this is a safety net.
    while (this.game.uiRoot.firstChild) {
      this.game.uiRoot.removeChild(this.game.uiRoot.firstChild);
    }
  }

  dispose() {
    if (this.current) {
      this.current.scene.dispose();
      this.current = undefined;
    }
    this.clearUi();
  }
}
